import { RotateCcw, Volume2, Youtube } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { cn } from '@/shared/lib/cn';

type YouTubePlayerState = -1 | 0 | 1 | 2 | 3 | 5;

export interface YouTubePlayer {
  destroy: () => void;
  getPlayerState: () => YouTubePlayerState;
  loadPlaylist: (options: {
    list: string;
    listType: 'playlist';
    index?: number;
    startSeconds?: number;
  }) => void;
  mute: () => void;
  nextVideo: () => void;
  playVideo: () => void;
  setLoop: (loopPlaylists: boolean) => void;
  setVolume: (volume: number) => void;
  unMute: () => void;
}

interface YouTubePlayerEvent {
  target: YouTubePlayer;
}

interface YouTubePlayerStateEvent extends YouTubePlayerEvent {
  data: YouTubePlayerState;
}

export interface YouTubePlayerErrorEvent extends YouTubePlayerEvent {
  data: number;
}

interface YouTubePlayerOptions {
  events: {
    onAutoplayBlocked?: (event: YouTubePlayerEvent) => void;
    onError?: (event: YouTubePlayerErrorEvent) => void;
    onReady?: (event: YouTubePlayerEvent) => void;
    onStateChange?: (event: YouTubePlayerStateEvent) => void;
  };
  height: string;
  playerVars: {
    autoplay: 0 | 1;
    controls: 0 | 1;
    enablejsapi: 1;
    list: string;
    listType: 'playlist';
    loop: 1;
    origin: string;
    playsinline: 1;
    rel: 0;
  };
  width: string;
}

interface YouTubeNamespace {
  Player: new (element: HTMLElement, options: YouTubePlayerOptions) => YouTubePlayer;
  PlayerState: {
    ENDED: 0;
    PLAYING: 1;
  };
}

declare global {
  interface Window {
    YT?: YouTubeNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

export interface YoutubePlaylistPlayerProps {
  playlistId: string;
  autoplay?: boolean;
  withSound?: boolean;
  showControls?: boolean;
  className?: string;
  frameClassName?: string;
  title?: string;
  onReady?: (player: YouTubePlayer) => void;
  onError?: (event: YouTubePlayerErrorEvent) => void;
}

type PlayerStatus = 'idle' | 'loading' | 'ready' | 'needs-interaction' | 'playing' | 'error';

const YOUTUBE_IFRAME_API_SRC = 'https://www.youtube.com/iframe_api';
const ready_callbacks: Array<() => void> = [];
let api_request: Promise<void> | null = null;

function loadYouTubeIframeApi(): Promise<void> {
  if (window.YT?.Player) return Promise.resolve();
  if (api_request) return api_request;

  api_request = new Promise<void>((resolve, reject) => {
    ready_callbacks.push(resolve);
    window.onYouTubeIframeAPIReady = () => {
      const callbacks = ready_callbacks.splice(0);
      callbacks.forEach((callback) => {
        callback();
      });
    };

    const existing_script = document.querySelector<HTMLScriptElement>(
      `script[src="${YOUTUBE_IFRAME_API_SRC}"]`,
    );
    if (existing_script) return;

    const script = document.createElement('script');
    script.src = YOUTUBE_IFRAME_API_SRC;
    script.async = true;
    script.onerror = () => {
      reject(new Error('No se pudo cargar la API de YouTube.'));
    };
    document.head.appendChild(script);
  });

  return api_request;
}

export function YoutubePlaylistPlayer({
  playlistId,
  autoplay = true,
  withSound = true,
  showControls = true,
  className,
  frameClassName,
  title = 'Playlist de YouTube',
  onReady,
  onError,
}: YoutubePlaylistPlayerProps): JSX.Element {
  const container_id = useId().replace(/:/g, '');
  const host_ref = useRef<HTMLDivElement | null>(null);
  const player_ref = useRef<YouTubePlayer | null>(null);
  const blocked_timer_ref = useRef<number | null>(null);
  const [status, set_status] = useState<PlayerStatus>('idle');
  const normalized_playlist_id = playlistId.trim();

  const clearBlockedTimer = useCallback(() => {
    if (blocked_timer_ref.current === null) return;
    window.clearTimeout(blocked_timer_ref.current);
    blocked_timer_ref.current = null;
  }, []);

  const startPlayback = useCallback(() => {
    const player = player_ref.current;
    if (!player) return;

    clearBlockedTimer();
    try {
      if (withSound) {
        player.unMute();
        player.setVolume(100);
      } else {
        player.mute();
      }
      player.playVideo();
      blocked_timer_ref.current = window.setTimeout(() => {
        if (player.getPlayerState() !== 1) {
          set_status('needs-interaction');
        }
      }, 2_200);
    } catch {
      set_status('needs-interaction');
    }
  }, [clearBlockedTimer, withSound]);

  useEffect(() => {
    if (!normalized_playlist_id) {
      set_status('error');
      return;
    }

    let is_mounted = true;
    set_status('loading');

    void loadYouTubeIframeApi()
      .then(() => {
        if (!is_mounted || !host_ref.current || !window.YT?.Player) return;

        player_ref.current = new window.YT.Player(host_ref.current, {
          height: '100%',
          width: '100%',
          playerVars: {
            autoplay: autoplay ? 1 : 0,
            controls: showControls ? 1 : 0,
            enablejsapi: 1,
            list: normalized_playlist_id,
            listType: 'playlist',
            loop: 1,
            origin: window.location.origin,
            playsinline: 1,
            rel: 0,
          },
          events: {
            onReady: (event) => {
              event.target.setLoop(true);
              onReady?.(event.target);
              set_status('ready');
              if (autoplay) startPlayback();
            },
            onStateChange: (event) => {
              if (event.data === 1) {
                clearBlockedTimer();
                set_status('playing');
                return;
              }

              if (event.data === 0) {
                window.setTimeout(() => {
                  if (event.target.getPlayerState() !== 0) return;
                  event.target.nextVideo();
                  event.target.playVideo();
                }, 1_800);
              }
            },
            onAutoplayBlocked: () => {
              clearBlockedTimer();
              set_status('needs-interaction');
            },
            onError: (event) => {
              clearBlockedTimer();
              set_status('error');
              onError?.(event);
            },
          },
        });
      })
      .catch(() => {
        if (is_mounted) set_status('error');
      });

    return () => {
      is_mounted = false;
      clearBlockedTimer();
      player_ref.current?.destroy();
      player_ref.current = null;
    };
  }, [
    autoplay,
    clearBlockedTimer,
    normalized_playlist_id,
    onError,
    onReady,
    showControls,
    startPlayback,
  ]);

  const has_playlist = normalized_playlist_id.length > 0;
  const show_start_overlay = status === 'needs-interaction' || (!autoplay && status === 'ready');
  const show_error = status === 'error';

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-[8px] border border-[#d7e5f3] bg-[#07195a] text-white shadow-[0_8px_18px_rgba(19,39,80,0.12)]',
        className,
      )}
    >
      <div className={cn('aspect-video w-full bg-black', frameClassName)}>
        {has_playlist ? (
          <div
            id={`youtube-playlist-${container_id}`}
            ref={host_ref}
            className="h-full w-full"
            title={title}
          />
        ) : null}
      </div>

      {status === 'loading' ? (
        <div className="absolute inset-0 grid place-items-center bg-[#07195a]">
          <div className="flex items-center gap-3 text-[22px] font-black uppercase">
            <Youtube className="h-9 w-9 text-[#62d6d1]" aria-hidden />
            Cargando playlist
          </div>
        </div>
      ) : null}

      {show_start_overlay ? (
        <div className="absolute inset-0 grid place-items-center bg-[#07195a]/95 px-8 text-center">
          <div>
            <div className="mx-auto mb-5 grid h-20 w-20 place-items-center rounded-full bg-[#62d6d1] text-[#07195a]">
              <Volume2 className="h-10 w-10" aria-hidden />
            </div>
            <button
              type="button"
              className="rounded-[8px] bg-white px-8 py-5 text-[30px] font-black uppercase leading-none text-[#07195a] shadow-[0_8px_18px_rgba(0,0,0,0.22)] transition hover:bg-[#e7fbfa]"
              onClick={() => {
                set_status('loading');
                startPlayback();
              }}
            >
              Iniciar sala de espera
            </button>
            <p className="mt-4 text-[16px] font-bold text-[#d8efef]">
              Se activara la playlist con sonido.
            </p>
          </div>
        </div>
      ) : null}

      {show_error ? (
        <div className="absolute inset-0 grid place-items-center bg-[#07195a] px-8 text-center">
          <div>
            <div className="mx-auto mb-4 grid h-16 w-16 place-items-center rounded-full bg-white/10">
              <RotateCcw className="h-8 w-8 text-[#62d6d1]" aria-hidden />
            </div>
            <p className="text-[24px] font-black uppercase leading-tight">
              {has_playlist
                ? 'No se pudo reproducir la playlist, contacta a soporte'
                : 'Playlist de YouTube sin configurar'}
            </p>
            <p className="mt-3 text-[15px] font-bold text-[#cfe2e9]">
              {has_playlist
                ? 'La sala continua mostrando los turnos.'
                : 'Agrega el ID de playlist para mostrar video.'}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
